'use client';

import Link from 'next/link';
import { useParams } from 'next/navigation';
import { toast } from 'react-toastify';

import { ActionButtons } from '@/components/molecules';
import { ModalWrapper } from '@/components/organisms';
import { getMatchupStatusApiValue, MatchStatusApiMap, MatchStatusEnum, MatchupStatusEnum } from '@/domain/constants/constants';
import { useUpdateMatchMutation } from '@/hooks/matches/useUpdateMatch';
import { useUpdateMatchup } from '@/hooks/matches/useUpdateMatchup';
import { useRotationsInfoQuery } from '@/hooks/tie-breaker/useTieBreakerRotationsInfo';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	matchupId: string;
	isTie: boolean;
	tieBreakerMatchId?: string;
}

export const CompleteMatchupModal = ({ isOpen, onClose, matchupId, isTie, tieBreakerMatchId }: Props) => {
	const params = useParams<{ teamLeagueId: string }>();
	const updateMatchupMutation = useUpdateMatchup();
	const updateMatchMutation = useUpdateMatchMutation({
		matchupId: matchupId
	});

	// Check if tie-breaker lineup is set
	const { data: rotationsInfo } = useRotationsInfoQuery(tieBreakerMatchId || '');

	const isTeamTwoLineupSet = Array.isArray(rotationsInfo?.data?.teamTwo) && rotationsInfo.data.teamTwo.length > 0;
	const isTeamOneLineupSet = Array.isArray(rotationsInfo?.data?.teamOne) && rotationsInfo.data.teamOne.length > 0;
	const isTieBreakerLineupSet = isTeamOneLineupSet && isTeamTwoLineupSet;

	const handleCompleteMatchup = async () => {
		try {
			// If there's a tie, check if lineup is set before proceeding
			if (isTie && !isTieBreakerLineupSet) {
				toast.error('Both teams need to set their lineup before activating the tie-breaker match.');
				return;
			}

			// If there's a tie, set tie-breaker match to IN_PROGRESS
			if (isTie && tieBreakerMatchId) {
				await updateMatchMutation.mutateAsync({
					matchId: tieBreakerMatchId,
					updateData: {
						matchStatus: MatchStatusApiMap[MatchStatusEnum.IN_PROGRESS_MATCH_STATUS]
					}
				});
				toast.success('Tie-breaker match set to in progress!');
			} else {
				await updateMatchupMutation.mutateAsync({
					matchup_uuid: matchupId,
					matchup_status: getMatchupStatusApiValue(MatchupStatusEnum.COMPLETED_MATCHUP_STATUS)
				});
				toast.success('Matchup completed successfully!');
			}

			onClose();
		} catch (_error) {
			toast.error('Failed to complete matchup. Please try again.');
			onClose();
		}
	};

	if (!isOpen) return null;

	const isPending = updateMatchMutation.isPending || updateMatchupMutation.isPending;

	return (
		<ModalWrapper
			title={isTie ? 'Activate Tie-Breaker' : 'Complete Matchup'}
			onClose={onClose}
			className="max-w-[500px]"
			closeOnOutsideClick={!isPending}
		>
			<hr className="text-gray-400 my-4" />
			<div className="flex flex-col gap-4 px-2">
				<div className="flex flex-col gap-3">
					<p className="text-base font-medium text-gray-900">
						{isTie ? 'Time to activate the tie-breaker match!' : 'Are you sure you want to complete this matchup?'}
					</p>
					<p className="text-sm text-gray-600">
						{isTie
							? 'The matchup is tied! A tie-breaker match will be activated to determine the winner.'
							: 'All matches have been played. The matchup will be marked as completed.'}
					</p>

					{/* Show warning if tie-breaker lineup is not set */}
					{isTie && !isTieBreakerLineupSet ? (
						<div className="mt-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
							<p className="text-sm font-medium text-orange-800">Warning: Lineup not set for tie-breaker</p>
							<p className="text-xs text-orange-600 mt-1">
								Both teams need to set their lineup before activating the tie-breaker match.
							</p>
							<div className="mt-2">
								<Link
									href={`/team-leagues/${params.teamLeagueId}/matchup/${matchupId}/tie-breaker`}
									onClick={onClose}
									className="inline-flex items-center text-xs font-medium text-orange-700 hover:text-orange-800 underline"
								>
									Set up tie-breaker lineup →
								</Link>
							</div>
						</div>
					) : null}
				</div>

				<ActionButtons
					className="flex justify-end gap-3 pt-4"
					closeButtonClassName="w-32 text-gray-700"
					submitButtonClassName="text-white w-fit"
					onClose={() => {
						if (!isPending) {
							onClose();
						}
					}}
					onSubmit={handleCompleteMatchup}
					submitButtonText={isPending ? 'Completing...' : isTie ? 'Play Tie Breaker' : 'Complete'}
					closeButtonText="Cancel"
					submitButtonDisabled={isPending || (isTie && !isTieBreakerLineupSet)}
				/>
			</div>
		</ModalWrapper>
	);
};
